import React, { createContext, useContext, useReducer, useEffect } from 'react';
import { Trip, Companion, isPaymentPaid } from '../types';
import { useAuth } from './AuthContext';
import { tripService } from '../services/trips';
import { companionService } from '../services/companions';

interface AppState {
  trips: Trip[];
  companions: Companion[];
  loading: {
    trips: boolean;
    companions: boolean;
  };
  error: {
    trips: string | null;
    companions: string | null;
  };
}

type AppAction =
  | { type: 'SET_LOADING'; payload: { key: keyof AppState['loading']; value: boolean } }
  | { type: 'SET_ERROR'; payload: { key: keyof AppState['error']; value: string | null } }
  | { type: 'SET_TRIPS'; payload: Trip[] }
  | { type: 'SET_COMPANIONS'; payload: Companion[] }
  | { type: 'ADD_TRIP'; payload: Trip }
  | { type: 'UPDATE_TRIP'; payload: Trip }
  | { type: 'ADD_COMPANION'; payload: Companion }
  | { type: 'UPDATE_COMPANION'; payload: Companion }
  | { type: 'DELETE_COMPANION'; payload: number }
  | { type: 'RESET_STATE' };

const initialState: AppState = {
  trips: [],
  companions: [],
  loading: {
    trips: false,
    companions: false,
  },
  error: {
    trips: null,
    companions: null,
  },
};

function appReducer(state: AppState, action: AppAction): AppState {
  switch (action.type) {
    case 'SET_LOADING':
      return {
        ...state,
        loading: {
          ...state.loading,
          [action.payload.key]: action.payload.value,
        },
      };
    case 'SET_ERROR':
      return {
        ...state,
        error: {
          ...state.error,
          [action.payload.key]: action.payload.value,
        },
      };
    case 'SET_TRIPS':
      return {
        ...state,
        trips: action.payload,
        error: { ...state.error, trips: null },
      };
    case 'SET_COMPANIONS':
      return {
        ...state,
        companions: action.payload,
        error: { ...state.error, companions: null },
      };
    case 'ADD_TRIP':
      return {
        ...state,
        trips: [action.payload, ...state.trips],
      };
    case 'UPDATE_TRIP':
      return {
        ...state,
        trips: state.trips.map(trip =>
          trip.id === action.payload.id ? action.payload : trip
        ),
      };
    case 'ADD_COMPANION':
      return {
        ...state,
        companions: [...state.companions, action.payload],
      };
    case 'UPDATE_COMPANION':
      return {
        ...state,
        companions: state.companions.map(companion =>
          companion.id === action.payload.id ? action.payload : companion
        ),
      };
    case 'DELETE_COMPANION':
      return {
        ...state,
        companions: state.companions.filter(companion => companion.id !== action.payload),
      };
    case 'RESET_STATE':
      return initialState;
    default:
      return state;
  }
}

interface AppContextValue extends AppState {
  // Trips
  loadTrips: () => Promise<void>;
  refreshTrips: () => Promise<void>;
  addTrip: (trip: Trip) => void;
  updateTrip: (trip: Trip) => void;
  
  // Companions
  loadCompanions: () => Promise<void>;
  refreshCompanions: () => Promise<void>;
  addCompanion: (companion: Companion) => void;
  updateCompanion: (companion: Companion) => void;
  deleteCompanion: (id: number) => Promise<void>;
  
  // Computed values
  currentTrip: Trip | null;
  pastTrips: Trip[];
}

const AppContext = createContext<AppContextValue | undefined>(undefined);

export function AppProvider({ children }: { children: React.ReactNode }) {
  const [state, dispatch] = useReducer(appReducer, initialState);
  const { user, isAuthenticated } = useAuth();

  // Load trips
  const loadTrips = async () => {
    if (!isAuthenticated) return;
    
    try {
      dispatch({ type: 'SET_LOADING', payload: { key: 'trips', value: true } });
      dispatch({ type: 'SET_ERROR', payload: { key: 'trips', value: null } });
      
      console.log('🚀 AppContext: Loading trips...');
      const trips = await tripService.getTrips();
      
      dispatch({ type: 'SET_TRIPS', payload: trips });
      console.log('✅ AppContext: Trips loaded successfully:', trips.length);
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to load trips';
      console.error('❌ AppContext: Error loading trips:', errorMessage);
      dispatch({ type: 'SET_ERROR', payload: { key: 'trips', value: errorMessage } });
    } finally {
      dispatch({ type: 'SET_LOADING', payload: { key: 'trips', value: false } });
    }
  };

  // Load companions
  const loadCompanions = async () => {
    if (!isAuthenticated) return;
    
    try {
      dispatch({ type: 'SET_LOADING', payload: { key: 'companions', value: true } });
      dispatch({ type: 'SET_ERROR', payload: { key: 'companions', value: null } });
      
      console.log('🚀 AppContext: Loading companions...');
      const companions = await companionService.getCompanions();
      
      dispatch({ type: 'SET_COMPANIONS', payload: companions });
      console.log('✅ AppContext: Companions loaded successfully:', companions.length);
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to load companions';
      console.error('❌ AppContext: Error loading companions:', errorMessage);
      dispatch({ type: 'SET_ERROR', payload: { key: 'companions', value: errorMessage } });
    } finally {
      dispatch({ type: 'SET_LOADING', payload: { key: 'companions', value: false } });
    }
  };

  // Refresh functions
  const refreshTrips = loadTrips;
  const refreshCompanions = loadCompanions;

  // Add/Update functions
  const addTrip = (trip: Trip) => {
    dispatch({ type: 'ADD_TRIP', payload: trip });
  };

  const updateTrip = (trip: Trip) => {
    dispatch({ type: 'UPDATE_TRIP', payload: trip });
  };

  const addCompanion = (companion: Companion) => {
    dispatch({ type: 'ADD_COMPANION', payload: companion });
  };

  const updateCompanion = (companion: Companion) => {
    dispatch({ type: 'UPDATE_COMPANION', payload: companion });
  };

  const deleteCompanion = async (id: number) => {
    try {
      console.log('🗑️ AppContext: Deleting companion:', id);
      await companionService.deleteCompanion(id);
      
      dispatch({ type: 'DELETE_COMPANION', payload: id });
      console.log('✅ AppContext: Companion deleted successfully');
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to delete companion';
      console.error('❌ AppContext: Error deleting companion:', errorMessage);
      throw error;
    }
  };

  // Computed values
  const currentTrip = state.trips.find(trip => {
    const now = new Date();
    const startDate = new Date(trip.start_date);
    const endDate = new Date(trip.end_date);
    return startDate <= now && now <= endDate && isPaymentPaid(trip.payment_status_id);
  }) || null;

  const pastTrips = state.trips.filter(trip => {
    if (trip === currentTrip) return false;
    return true;
  }).sort((a, b) => new Date(b.start_date).getTime() - new Date(a.start_date).getTime());

  // Load data when user changes
  useEffect(() => {
    if (isAuthenticated && user) {
      console.log('🔄 AppContext: User authenticated, loading data...');
      loadTrips();
      loadCompanions();
    } else {
      console.log('🔄 AppContext: User not authenticated, resetting state...');
      dispatch({ type: 'RESET_STATE' });
    }
  }, [isAuthenticated, user?.id]);

  const value: AppContextValue = {
    ...state,
    loadTrips,
    refreshTrips,
    addTrip,
    updateTrip,
    loadCompanions,
    refreshCompanions,
    addCompanion,
    updateCompanion,
    deleteCompanion,
    currentTrip,
    pastTrips,
  };

  return (
    <AppContext.Provider value={value}>
      {children}
    </AppContext.Provider>
  );
}

export function useApp() {
  const context = useContext(AppContext);
  if (!context) {
    throw new Error('useApp must be used within an AppProvider');
  }
  return context;
}
